const randomInt = (min, max) =>
	Math.floor(Math.random() * (max - min + 1)) + min;

const initSearchToggle = () => {
	// The inline search field is used in both the desktop nav and the
	// mobile menu, so each .nav-search instance is wired up independently.
	document.querySelectorAll(".nav-search").forEach((form) => {
		const toggle = form.querySelector(".nav-search__toggle");
		const input = form.querySelector(".nav-search__input");
		const clear = form.querySelector(".nav-search__clear");
		if (!toggle || !input) return;

		const isOpen = () => !input.hidden;

		const updateClear = () => {
			if (clear) clear.hidden = input.value.length === 0;
		};

		const openSearch = () => {
			toggle.hidden = true;
			input.hidden = false;
			toggle.setAttribute("aria-expanded", "true");
			input.focus();
			updateClear();
		};

		const closeSearch = () => {
			input.hidden = true;
			toggle.hidden = false;
			toggle.setAttribute("aria-expanded", "false");
		};

		toggle.addEventListener("click", openSearch);

		if (clear) {
			input.addEventListener("input", updateClear);
			clear.addEventListener("click", () => {
				input.value = "";
				updateClear();
				input.focus();
			});
		}

		document.addEventListener("click", (event) => {
			if (!isOpen()) return;
			if (form.contains(event.target)) return;
			closeSearch();
		});

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && isOpen()) {
				closeSearch();
				toggle.focus();
			}
		});
	});
};

const initLogoIcon = () => {
	const icon = document.querySelector(".icon--logo");
	const headerRow = document.querySelector(".header__row");
	if (!icon || !headerRow) return;

	const readVar = (name) =>
		parseFloat(
			getComputedStyle(document.documentElement).getPropertyValue(name),
		);

	// --grid-col-count changes at the lg breakpoint (6 mobile, 10 desktop),
	// so the "near the end of the row" range is relative to it rather than
	// hardcoded to the desktop column count. The column is picked once
	// (like the old grid-column placement was) and kept fixed; only its
	// pixel translation is recomputed on resize, since the icon is
	// position:fixed and no longer gets that from the grid for free.
	const colCount = readVar("--grid-col-count") || 10;
	const column =
		randomInt(1, 10) === 1 ? 2 : randomInt(colCount - 4, colCount);

	icon.style.setProperty("--icon-rotate", `${randomInt(-12, 12)}deg`);

	const position = () => {
		const marginPage = readVar("--margin-page") || 0;
		const gridGap = readVar("--grid-gap") || 0;
		const trackWidth =
			(window.innerWidth - marginPage * 2 - gridGap * (colCount - 1)) /
			colCount;
		let left = marginPage + (column - 1) * (trackWidth + gridGap);

		// Vertical placement mirrors the old align-items: center behaviour
		// within the header's first grid row.
		const rowRect = headerRow.getBoundingClientRect();
		const top = rowRect.top + rowRect.height / 2 - icon.offsetHeight / 2;

		icon.style.setProperty("--icon-left", `${left}px`);

		// When the cart badge is showing, pull the icon back in from the
		// right edge rather than letting it sit flush behind/under the badge.
		const cartCount = document.querySelector(".cart-count");
		const hasCartCount =
			cartCount && parseInt(cartCount.dataset.count, 10) > 0;
		if (hasCartCount) {
			const overflow =
				icon.getBoundingClientRect().right - (window.innerWidth - 100);
			if (overflow > 0) {
				left -= overflow;
				icon.style.setProperty("--icon-left", `${left}px`);
			}
		}
	};

	position();

	let resizeTimer;
	window.addEventListener("resize", () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(position, 150);
	});
};;

const withAutoplay = (src) => {
	try {
		const url = new URL(src, window.location.href);
		url.searchParams.set("autoplay", "1");
		url.searchParams.set("playsinline", "1");
		return url.toString();
	} catch {
		// Non-absolute or malformed src: hand it back untouched rather than
		// dropping the embed entirely.
		return src;
	}
};

const initFlexVideoRows = () => {
	// Rows with a poster image keep their oEmbed markup inert inside a
	// <template>, so no third-party player loads until someone asks for
	// one. The click that swaps it in is also the user gesture browsers
	// require before autoplay, so the video plays immediately rather than
	// showing a second play button.
	document.querySelectorAll("[data-flex-video]").forEach((figure) => {
		const template = figure.querySelector("[data-flex-video-embed]");
		const button = figure.querySelector("[data-flex-video-play]");
		if (!template || !button) return;

		button.addEventListener(
			"click",
			() => {
				const embed = template.content.cloneNode(true);
				embed.querySelectorAll("iframe").forEach((iframe) => {
					iframe.src = withAutoplay(iframe.src);
					iframe.setAttribute("allow", "autoplay; fullscreen; picture-in-picture");
				});

				const wrap = document.createElement("div");
				wrap.className = "flex-video__embed";
				wrap.appendChild(embed);

				button.remove();
				figure.appendChild(wrap);
				figure.classList.add("flex-video--playing");

				// Focus was on the button that just left the DOM; move it
				// into the player so keyboard users don't get dropped to
				// the top.
				const iframe = wrap.querySelector("iframe");
				if (iframe) iframe.focus();
			},
			{ once: true },
		);
	});
};

const initAccordionRows = () => {
	// Height reveal is pure CSS (the .is-open grid-row trick); this just
	// keeps aria-expanded and the collapsed panel's `inert` state in sync.
	document.querySelectorAll(".accordion-row__toggle").forEach((toggle) => {
		const row = toggle.closest(".page__row--accordion_row");
		const panel = row?.querySelector(".accordion-row__panel");
		if (!row || !panel) return;

		const apply = (open) => {
			row.classList.toggle("is-open", open);
			toggle.setAttribute("aria-expanded", String(open));
			panel.inert = !open;
		};

		apply(toggle.getAttribute("aria-expanded") === "true");

		toggle.addEventListener("click", () => {
			apply(toggle.getAttribute("aria-expanded") !== "true");
		});
	});
};

const initBooksMenu = () => {
	const trigger = document.querySelector(".nav-link--books");
	const panel = document.getElementById("books-menu");
	if (!trigger || !panel) return;

	const isOpen = () => !panel.hidden;

	const openMenu = () => {
		panel.hidden = false;
		trigger.setAttribute("aria-expanded", "true");
	};

	const closeMenu = () => {
		panel.hidden = true;
		trigger.setAttribute("aria-expanded", "false");
	};

	trigger.addEventListener("click", (event) => {
		event.preventDefault();
		if (isOpen()) {
			closeMenu();
		} else {
			openMenu();
		}
	});

	document.addEventListener("click", (event) => {
		if (!isOpen()) return;
		if (trigger.contains(event.target) || panel.contains(event.target))
			return;
		closeMenu();
	});

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape" && isOpen()) {
			closeMenu();
			trigger.focus();
		}
	});
};

const initMobileMenu = () => {
	const toggle = document.querySelector(".mobile-menu__toggle");
	const close = document.querySelector(".mobile-menu__close");
	const panel = document.getElementById("mobile-menu");
	if (!toggle || !close || !panel) return;

	const isOpen = () => !panel.hidden;

	const openMenu = () => {
		panel.hidden = false;
		toggle.setAttribute("aria-expanded", "true");
		close.focus();
	};

	const closeMenu = () => {
		panel.hidden = true;
		toggle.setAttribute("aria-expanded", "false");
		toggle.focus();
	};

	toggle.addEventListener("click", openMenu);
	close.addEventListener("click", closeMenu);

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape" && isOpen()) closeMenu();
	});
};

const initCartDrawer = () => {
	const toggle = document.querySelector(".cart-drawer__toggle");
	const close = document.querySelector(".cart-drawer__close");
	const panel = document.getElementById("cart-drawer");
	if (!toggle || !close || !panel) return;

	const isOpen = () => !panel.hidden;

	const openDrawer = () => {
		panel.hidden = false;
		toggle.setAttribute("aria-expanded", "true");
		close.focus();
	};

	const closeDrawer = () => {
		panel.hidden = true;
		toggle.setAttribute("aria-expanded", "false");
	};

	toggle.addEventListener("click", openDrawer);
	close.addEventListener("click", closeDrawer);

	document.addEventListener("keydown", (event) => {
		if (event.key === "Escape" && isOpen()) closeDrawer();
	});

	document.addEventListener("click", (event) => {
		if (!isOpen()) return;
		if (panel.contains(event.target) || toggle.contains(event.target)) return;
		closeDrawer();
	});

	// Swaps in the fresh HTML a wc-ajax add/remove response returns (keyed
	// by CSS selector) and shows/hides the toggle based on the new count,
	// since it's hidden outright rather than just showing "(0)".
	const applyCartFragments = (fragments) => {
		Object.keys(fragments || {}).forEach((selector) => {
			const el = document.querySelector(selector);
			if (el) el.outerHTML = fragments[selector];
		});

		const count = document.querySelector(".cart-count");
		if (count) toggle.hidden = parseInt(count.dataset.count, 10) <= 0;

		// Fragments are rendered fresh in CAD (same PHP as the initial
		// drawer markup), so any previously detected non-CAD currency needs
		// re-applying to the new markup.
		reapplyCurrencyDisplay();
	};

	// WC_AJAX::add_to_cart()'s error response carries no message — the
	// real one (e.g. "this product can't be added, you already have a
	// pre-order...") is a WC notice sitting in the session, so it's
	// fetched separately and shown in the drawer instead of just
	// reporting that *something* was rejected.
	const showCartNotices = () => {
		if (typeof cartDrawerParams === "undefined" || !cartDrawerParams.noticesUrl) return;

		fetch(cartDrawerParams.noticesUrl, { credentials: "same-origin" })
			.then((response) => response.json())
			.then((data) => {
				const notices = document.querySelector(".cart-drawer__notices");
				if (notices && data.notices) notices.innerHTML = data.notices;
				openDrawer();
			})
			.catch((error) => console.error("Cart drawer: couldn't load cart notices.", error));
	};

	// A form this listener has already resubmitted natively (see the
	// fallback below) and shouldn't re-intercept the second time around.
	const bypassedForms = new WeakSet();

	// Every place this listener gives up and lets the browser's normal
	// submit proceed is otherwise silent — nothing is logged and no fetch
	// is attempted, so the console looks empty even though a specific,
	// diagnosable decision was made. This stashes why, reported on the
	// next page load (see reportCartDrawerFallback() in init()), since the
	// navigation that follows wipes the console before anyone can read it.
	const reportSilentBail = (reason) => {
		console.error("Cart drawer:", reason);
		try {
			sessionStorage.setItem("cartDrawerFallbackReason", reason);
		} catch (storageError) {
			// Private browsing / storage disabled — nothing else to do.
		}
	};

	// Intercepts the single-product add-to-cart form (both simple and
	// variable products render a form.cart) so a successful add opens the
	// drawer instead of reloading the page.
	document.addEventListener("submit", (event) => {
		const form = event.target;

		if (bypassedForms.has(form)) {
			bypassedForms.delete(form);
			return;
		}

		if (!form.classList.contains("cart") || typeof cartDrawerParams === "undefined") {
			return;
		}

		// A simple product's add-to-cart button carries its product id as
		// the button's own name="add-to-cart" value="<id>" rather than a
		// hidden input (variable products use a hidden input instead, so
		// their button has no name). new FormData(form) alone never
		// includes a submit button's name/value — only the newer
		// new FormData(form, submitter) overload does, and that overload
		// isn't universally supported — so it's appended by hand instead.
		const formData = new FormData(form);
		if (event.submitter && event.submitter.name) {
			formData.set(event.submitter.name, event.submitter.value);
		}

		// WC_Form_Handler::add_to_cart_action() runs on every request (it's
		// hooked on wp_loaded, not scoped to any endpoint) and reads
		// $_REQUEST['add-to-cart'] unconditionally — so leaving it in this
		// POST would add the item a second time via that classic handler,
		// on top of the AJAX add below. WC_AJAX::add_to_cart() instead
		// expects the id in "product_id", so it's moved rather than dropped.
		const addToCartId = formData.get("add-to-cart");
		formData.delete("add-to-cart");
		if (addToCartId && !formData.get("product_id")) {
			formData.set("product_id", addToCartId);
		}

		// WC_AJAX::add_to_cart() expects a variable product's selected
		// *variation* id in "product_id" (it has no separate variation_id
		// field, unlike the classic non-AJAX form handler) — so a variation
		// form has to swap it in. If no variation is resolved yet (e.g. the
		// browser's native required-field check hasn't run), fall back to a
		// normal submit rather than sending an id the endpoint can't use.
		if (form.classList.contains("variations_form")) {
			const variationId = formData.get("variation_id");
			if (!variationId || variationId === "0") {
				reportSilentBail("No variation resolved yet (variation_id was " + JSON.stringify(variationId) + "); letting the browser submit normally.");
				return;
			}
			formData.set("product_id", variationId);
		}

		if (!formData.get("product_id")) {
			const submitterInfo = event.submitter
				? event.submitter.tagName + " name=" + JSON.stringify(event.submitter.name) + " value=" + JSON.stringify(event.submitter.value)
				: "none";
			reportSilentBail("No product_id resolved (event.submitter: " + submitterInfo + "); letting the browser submit normally.");
			return;
		}

		event.preventDefault();

		const submitButton = form.querySelector(".single_add_to_cart_button");
		if (submitButton) submitButton.disabled = true;

		const fallBackToNormalSubmit = (reason) => {
			reportSilentBail(reason);
			bypassedForms.add(form);
			form.requestSubmit(event.submitter);
		};

		fetch(cartDrawerParams.addToCartUrl, {
			method: "POST",
			credentials: "same-origin",
			body: formData,
		})
			.then((response) => response.text())
			.then((text) => {
				let data;
				try {
					data = JSON.parse(text);
				} catch (parseError) {
					// A caching layer, extension, or transient server hiccup can
					// return a non-JSON (or empty) body — fall back to a normal
					// submit rather than silently doing nothing.
					fallBackToNormalSubmit("Unexpected add-to-cart response: " + JSON.stringify(text));
					return;
				}

				if (data.error) {
					showCartNotices();
					return;
				}

				applyCartFragments(data.fragments);
				openDrawer();
			})
			.catch((error) => {
				fallBackToNormalSubmit("add-to-cart request failed: " + error);
			})
			.finally(() => {
				if (submitButton) submitButton.disabled = false;
			});
	});

	// Removing an item stays inside the drawer rather than navigating to
	// the cart page, so it never surfaces WooCommerce's own "item removed"
	// page notice.
	panel.addEventListener("click", (event) => {
		const button = event.target.closest(".cart-drawer__item-remove");
		if (!button || typeof cartDrawerParams === "undefined") return;

		button.disabled = true;

		fetch(cartDrawerParams.removeFromCartUrl, {
			method: "POST",
			credentials: "same-origin",
			body: new URLSearchParams({ cart_item_key: button.dataset.cartItemKey }),
		})
			.then((response) => response.text())
			.then((text) => {
				let data;
				try {
					data = JSON.parse(text);
				} catch (parseError) {
					console.error("Cart drawer: unexpected remove-from-cart response.", text);
					button.disabled = false;
					return;
				}
				applyCartFragments(data.fragments);
			})
			.catch((error) => {
				console.error("Cart drawer: remove-from-cart request failed.", error);
				button.disabled = false;
			});
	});
};

const initPriceDropdown = () => {
	// The visible UI proxies clicks into WooCommerce's own (visually
	// hidden) <select>, dispatching a real change event so
	// wc-add-to-cart-variation.js keeps handling price/stock matching.
	document.querySelectorAll(".price-dropdown").forEach((form) => {
		const toggle = form.querySelector("[data-price-dropdown-toggle]");
		const list = form.querySelector("[data-price-dropdown-list]");
		const selectedLabel = form.querySelector("[data-price-dropdown-selected]");
		if (!toggle || !list || !selectedLabel) return;

		const attrName = list.dataset.priceDropdownAttribute;
		const select = form.querySelector(`select[name="${attrName}"]`);
		if (!select) return;

		const syncLabel = () => {
			const option = select.options[select.selectedIndex];
			selectedLabel.textContent = option ? option.textContent : "";
		};

		const isOpen = () => !list.hidden;

		const openList = () => {
			list.hidden = false;
			toggle.setAttribute("aria-expanded", "true");
		};

		const closeList = () => {
			list.hidden = true;
			toggle.setAttribute("aria-expanded", "false");
		};

		toggle.addEventListener("click", () => {
			if (isOpen()) {
				closeList();
			} else {
				openList();
			}
		});

		list.querySelectorAll("[data-price-dropdown-option]").forEach((button) => {
			button.addEventListener("click", () => {
				select.value = button.dataset.priceDropdownOption;
				select.dispatchEvent(new Event("change", { bubbles: true }));
				syncLabel();
				closeList();
			});
		});

		document.addEventListener("click", (event) => {
			if (!isOpen()) return;
			if (toggle.contains(event.target) || list.contains(event.target))
				return;
			closeList();
		});

		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape" && isOpen()) {
				closeList();
				toggle.focus();
			}
		});

		syncLabel();
	});
};

// Shows prices in the visitor's detected currency on pages that can be
// full-page-cached (product page, cart drawer), where the conversion
// can't be baked into the server-rendered HTML — see
// snippets/currency-display.php for why, and for the cart/checkout pages
// that convert server-side instead since they're never cached.
let currentCurrencyInfo = null;

const CURRENCY_CACHE_KEY = "ipCurrencyInfo";
const CURRENCY_CACHE_TTL_MS = 60 * 60 * 1000;

const getCachedCurrencyInfo = () => {
	try {
		const raw = sessionStorage.getItem(CURRENCY_CACHE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (Date.now() - parsed.fetchedAt > CURRENCY_CACHE_TTL_MS) return null;
		return parsed;
	} catch (storageError) {
		return null;
	}
};

const setCachedCurrencyInfo = (info) => {
	try {
		sessionStorage.setItem(
			CURRENCY_CACHE_KEY,
			JSON.stringify({ ...info, fetchedAt: Date.now() }),
		);
	} catch (storageError) {
		// Private browsing / storage disabled — just means we re-fetch next time.
	}
};

// Rewrites every WooCommerce-rendered price on the page (the
// .woocommerce-Price-amount markup wc_price() always emits) into the
// visitor's detected currency. Safe to call repeatedly — a variation
// change, a cart-drawer fragment refresh — since each amount's original
// CAD text is stashed once in a data attribute, so later calls always
// convert from the true source number rather than a previously converted
// one. Checkout's real total is explicitly skipped: it gets its own
// clearly-labeled estimate row rendered server-side instead, and must
// never be silently relabeled.
const applyCurrencyToPrices = ({ currency, symbol, rate }) => {
	document.querySelectorAll(".woocommerce-Price-amount.amount").forEach((amountEl) => {
		if (amountEl.closest("form.checkout, #order_review")) return;

		const bdi = amountEl.querySelector("bdi") || amountEl;
		if (bdi.dataset.cadText === undefined) {
			bdi.dataset.cadText = bdi.textContent.trim();
		}

		const raw = parseFloat(bdi.dataset.cadText.replace(/[^0-9.-]/g, ""));
		if (Number.isNaN(raw)) return;

		// snippets/currency-display.php renders a dedicated .price-currency-
		// code label next to simple price amounts (product page, cart page)
		// — reuse that instead of embedding the code into the amount a
		// second time. Sale prices (<del>/<ins>) have no such sibling, so
		// each amount falls back to carrying its own inline code.
		const label =
			amountEl.previousElementSibling?.classList?.contains("price-currency-code")
				? amountEl.previousElementSibling
				: null;

		if (currency === "CAD") {
			bdi.textContent = bdi.dataset.cadText;
			if (label) label.textContent = "CAD";
			return;
		}

		const converted = (raw * rate).toFixed(2);

		// get_woocommerce_currency_symbol() (the source of `symbol`, via the
		// wc-ajax detect endpoint) returns an HTML entity like "&#36;", the
		// same way wc_price() always renders it — textContent would show
		// that entity as literal text instead of the character it encodes,
		// so this has to go through innerHTML instead.
		if (label) {
			label.textContent = currency;
			bdi.innerHTML = `${symbol}${converted}`;
		} else {
			bdi.innerHTML = `${currency} ${symbol}${converted}`;
		}
	});
};

const reapplyCurrencyDisplay = () => {
	if (currentCurrencyInfo) applyCurrencyToPrices(currentCurrencyInfo);
};

const initCurrencyDisplay = () => {
	if (typeof ipCurrencyParams === "undefined" || !ipCurrencyParams.detectUrl) return;

	const cached = getCachedCurrencyInfo();
	if (cached) {
		currentCurrencyInfo = cached;
		applyCurrencyToPrices(cached);
		return;
	}

	fetch(ipCurrencyParams.detectUrl, { credentials: "same-origin" })
		.then((response) => response.json())
		.then((info) => {
			currentCurrencyInfo = info;
			setCachedCurrencyInfo(info);
			applyCurrencyToPrices(info);
		})
		.catch((error) => {
			console.error("Currency display: couldn't detect visitor currency.", error);
		});
};

// WooCommerce's own wc-add-to-cart-variation.js replaces the contents of
// .single_variation_wrap (with a freshly formatted CAD price) whenever a
// variation is matched — this theme's price-dropdown doesn't hook its
// jQuery events, so a MutationObserver is the simplest way to notice and
// re-convert it.
const observeVariationPrice = () => {
	document.querySelectorAll(".single_variation_wrap").forEach((wrap) => {
		// reapplyCurrencyDisplay() rewrites text inside this same subtree,
		// which is itself a mutation — without disconnecting first, that
		// rewrite would retrigger this callback forever. Disconnecting
		// before writing and reconnecting after only resumes listening for
		// the *next* WooCommerce-driven variation change.
		const observer = new MutationObserver(() => {
			observer.disconnect();
			reapplyCurrencyDisplay();
			observer.observe(wrap, { childList: true, subtree: true });
		});
		observer.observe(wrap, { childList: true, subtree: true });
	});
};

// Reports why the add-to-cart drawer fell back to a normal submit on the
// *previous* page — that navigation wipes the console before a log line
// right before it is likely to be noticed, so it's handed off here instead.
const reportCartDrawerFallback = () => {
	try {
		const reason = sessionStorage.getItem("cartDrawerFallbackReason");
		if (reason) {
			sessionStorage.removeItem("cartDrawerFallbackReason");
			console.error("Cart drawer fell back to a normal add-to-cart submit:", reason);
		}
	} catch (storageError) {
		// Private browsing / storage disabled — nothing to report.
	}
};

const init = () => {
	reportCartDrawerFallback();
	initSearchToggle();
	initLogoIcon();
	initFlexVideoRows();
	initAccordionRows();
	initBooksMenu();
	initMobileMenu();
	initCartDrawer();
	initPriceDropdown();
	observeVariationPrice();
	initCurrencyDisplay();
};

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", init);
} else {
	init();
}
